import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const STEPS = [
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-[#ebebeb]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
      </svg>
    ),
    title: 'Step 1: Search for Papers',
    body: 'Start by searching across PubMed, arXiv, OpenAlex, Europe PMC, and bioRxiv simultaneously. Enter any topic, method, or research question — Protovera searches all five databases in one go.',
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-[#ebebeb]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    title: 'Step 2: Build Your Workspace',
    body: 'Click "Add to Workspace" on the papers that are most relevant. Your workspace is the foundation — the more precise the selection, the better the AI outputs will be.',
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-[#ebebeb]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    title: 'Step 3: Generate a Protocol',
    body: 'With 3+ papers in your workspace, click "Generate Protocol" in the sidebar. Protovera produces a structured 6-section protocol with inline citations — all grounded in your papers, not generic AI output.',
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-[#ebebeb]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: 'More AI Tools in the Sidebar',
    body: 'Once your workspace is built, explore the AI Tools section in the sidebar: Hypothesis Generator, Grant Assistant, Literature Review, Knowledge Graph, Methods Extractor, and Chat with Paper.',
  },
];

export default function OnboardingModal() {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [completing, setCompleting] = useState(false);

  if (!user || user.is_onboarded !== false) return null;

  const isLast = step === STEPS.length - 1;

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await api.post('/api/auth/complete-onboarding');
    } catch (err) {
      console.error('Onboarding completion failed:', err);
    } finally {
      await refreshUser();
      setCompleting(false);
    }
  };

  const current = STEPS[step];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden"
        style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 8px 32px' }}
      >
        {/* Dark top bar */}
        <div className="bg-[#171717] px-6 py-4">
          <p className="text-xs font-medium text-white/50 uppercase tracking-widest">
            Getting started — step {step + 1} of {STEPS.length}
          </p>
          {/* Step dots */}
          <div className="flex gap-1.5 mt-2">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  background: i <= step ? '#fff' : 'rgba(255,255,255,0.2)',
                  width: i <= step ? '24px' : '12px',
                }}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-8 text-center">
          <div className="flex justify-center mb-4">{current.icon}</div>
          <h2 className="text-xl font-semibold text-[#171717] mb-3" style={{ letterSpacing: '-1.28px' }}>{current.title}</h2>
          <p className="text-sm text-[#4d4d4d] leading-relaxed">{current.body}</p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex justify-end" style={{ borderTop: '1px solid #ebebeb' }}>
          <div className="flex gap-2 pt-4">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="px-5 py-2 text-sm font-medium text-[#4d4d4d] rounded-md hover:text-[#171717] transition-colors"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              >
                Back
              </button>
            )}
            {isLast ? (
              <button
                onClick={handleComplete}
                disabled={completing}
                className="px-6 py-2 bg-[#171717] text-white text-sm font-medium rounded-md hover:bg-[#2e2e2e] disabled:opacity-60 transition-colors"
              >
                {completing ? 'Setting up...' : 'Get Started'}
              </button>
            ) : (
              <button
                onClick={() => setStep((s) => s + 1)}
                className="px-6 py-2 bg-[#171717] text-white text-sm font-medium rounded-md hover:bg-[#2e2e2e] transition-colors"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
